import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save } from 'lucide-react';
import { getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const ROLES = ['observers', 'users', 'system', 'managers', 'supervisors', 'receptionists', 'group_admins', 'group_owners', 'system_admins', 'system_owners'];

export default function Groups() {
  const qc = useQueryClient();
  const toast = useToast();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });
  const [domain, setDomain] = useState('');
  const active = domain || tenants.data?.[0]?.domain_uuid || '';
  const [showAdd, setShowAdd] = useState(false);
  const [showAddMember, setShowAddMember] = useState<string | null>(null);
  const [form, setForm] = useState<any>({});
  const [memberForm, setMemberForm] = useState<any>({ role: 'users' });

  const groups = useQuery<any[]>({
    queryKey: ['user-groups', active],
    queryFn: () => fJson(`/api/users-extended/groups?domain_uuid=${active}`),
    enabled: !!active,
  });
  const users = useQuery<any[]>({ queryKey: ['users-list'], queryFn: () => fJson('/api/users') });

  const create = useMutation({
    mutationFn: () => fJson('/api/users-extended/groups', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, domain_uuid: active }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-groups', active] }); setShowAdd(false); setForm({}); toast('success', 'Group created'); },
  });

  const addMember = useMutation({
    mutationFn: (group_uuid: string) => fJson(`/api/users-extended/groups/${group_uuid}/members`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(memberForm),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-groups', active] }); setShowAddMember(null); setMemberForm({ role: 'users' }); toast('success', 'Added'); },
  });
  const removeMember = useMutation({
    mutationFn: ({ group_uuid, extension_uuid }: any) => fJson(`/api/users-extended/groups/${group_uuid}/members/${extension_uuid}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-groups', active] }); toast('success', 'Removed'); },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Groups & Roles</h1>
        <div className="flex gap-2">
          <select className="input" value={active} onChange={e => setDomain(e.target.value)}>
            {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
          </select>
          <button onClick={() => setShowAdd(true)} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add Group</button>
        </div>
      </div>
      <p className="text-sm text-textSecondary">
        Group memberships drive RBAC across the tenant — barge-in, intercom, parking, IVR access, see-group-calls/recordings, presence visibility, and operations on other users.
        Each membership can override the group-level rights.
      </p>

      <div className="space-y-4">
        {(groups.data || []).map((g: any) => (
          <div key={g.group_uuid} className="card p-0 overflow-hidden">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-medium">{g.name}</div>
                <div className="text-xs text-textSecondary">{g.description || '—'}</div>
              </div>
              <button onClick={() => setShowAddMember(g.group_uuid)} className="btn-primary inline-flex items-center gap-1"><Plus size={14} /> Add member</button>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Member</th><th>Role</th>
                  <th>Barge-in</th><th>Intercom</th><th>Parking</th><th>IVR</th>
                  <th>See calls</th><th>See recs</th><th>Presence</th><th>Operations</th><th></th>
                </tr>
              </thead>
              <tbody>
                {(g.memberships || []).map((m: any) => (
                  <tr key={m.membership_uuid} className="hover:bg-elevated">
                    <td>{m.extension} — {m.user_name}</td>
                    <td className="text-xs">{m.role}</td>
                    <td><Check on={m.can_barge_in} /></td>
                    <td><Check on={m.can_intercom} /></td>
                    <td><Check on={m.allow_parking} /></td>
                    <td><Check on={m.allow_ivr} /></td>
                    <td><Check on={m.can_see_group_calls} /></td>
                    <td><Check on={m.can_see_group_recordings} /></td>
                    <td><Check on={m.show_my_presence} /></td>
                    <td><Check on={m.perform_operations} /></td>
                    <td><button onClick={() => removeMember.mutate({ group_uuid: g.group_uuid, extension_uuid: m.extension_uuid })} className="btn-ghost p-1 text-danger"><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!g.memberships?.length && <div className="p-4 text-center text-textSecondary text-sm">No members.</div>}
          </div>
        ))}
        {!groups.data?.length && <div className="card p-6 text-center text-textSecondary">No groups yet.</div>}
      </div>

      <Dialog open={showAdd} onClose={() => setShowAdd(false)} title="Add Group" size="md"
        footer={<><button onClick={() => setShowAdd(false)} className="btn-ghost">Cancel</button><button onClick={() => create.mutate()} className="btn-primary">Create</button></>}>
        <div className="space-y-3">
          <Field label="Name *"><input className="input" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Description"><input className="input" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
        </div>
      </Dialog>

      <Dialog open={!!showAddMember} onClose={() => setShowAddMember(null)} title="Add member" size="md"
        footer={<><button onClick={() => setShowAddMember(null)} className="btn-ghost">Cancel</button><button onClick={() => showAddMember && addMember.mutate(showAddMember)} className="btn-primary">Add</button></>}>
        <div className="space-y-3">
          <Field label="User *">
            <select className="input" value={memberForm.extension_uuid || ''} onChange={e => setMemberForm({ ...memberForm, extension_uuid: e.target.value })}>
              <option value="">— Choose —</option>
              {(users.data || []).filter((u: any) => u.domain_uuid === active).map((u: any) => (
                <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Role">
            <select className="input" value={memberForm.role || 'users'} onChange={e => setMemberForm({ ...memberForm, role: e.target.value })}>
              {ROLES.map(r => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-1">
            {[
              ['can_barge_in', 'Barge-in'],
              ['can_intercom', 'Intercom'],
              ['allow_parking', 'Allow parking'],
              ['allow_ivr', 'Allow IVR'],
              ['can_see_group_calls', 'See group calls'],
              ['can_see_group_recordings', 'See group recordings'],
              ['show_my_presence', 'Show my presence'],
              ['allow_change_presence', 'Allow change presence'],
              ['perform_operations', 'Perform operations'],
            ].map(([k, l]) => (
              <label key={k} className="text-sm flex items-center gap-2">
                <input type="checkbox" checked={!!memberForm[k]} onChange={e => setMemberForm({ ...memberForm, [k]: e.target.checked })} /> {l}
              </label>
            ))}
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function Check({ on }: { on: boolean | null | undefined }) { return on ? <span className="text-success">✓</span> : <span className="text-textSecondary">—</span>; }
function Field({ label, children }: any) { return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>; }
